/**
 * The bits of chart configuration both monthly charts share, kept out of the components so
 * the two cannot drift apart.
 */

/**
 * How much room to leave above the tallest bar, as a multiple of it.
 *
 * Not cosmetic. The plot is clipped to exactly the drawing area, so a label drawn above a
 * bar that reaches the top of the axis is cut off — and increasing the chart's margin does
 * not help, because the clip starts below the margin. Headroom on the axis is the only
 * thing that does.
 */
const HEADROOM = 1.15;

/**
 * An axis maximum with room for the labels, or undefined to let the chart decide.
 *
 * Computed from the *visible* series only, so switching a dominant series off lets the rest
 * use the full height instead of huddling at the bottom of a scale they no longer need.
 */
export const axisMaxWithHeadroom = (values: number[]): number | undefined => {
    const peak = values.length === 0 ? 0 : Math.max(...values);

    return peak <= 0 ? undefined : peak * HEADROOM;
};

/**
 * The same headroom, rounded up to a whole number.
 *
 * For a count of people. Left as a fraction, a clinic whose busiest month saw one patient
 * gets an axis running 0.0, 0.2, 0.4 -- and a fifth of a patient is not a thing. Pairs with
 * `COUNT_TICK_MIN_STEP` so the ticks in between stay whole too.
 */
export const countAxisMaxWithHeadroom = (values: number[]): number | undefined => {
    const max = axisMaxWithHeadroom(values);

    return max === undefined ? undefined : Math.ceil(max);
};

/** No tick closer together than one whole person. */
export const COUNT_TICK_MIN_STEP = 1;

/** All twelve ticks, always: the chart is a year, and a year with months missing is a lie. */
export const monthTickInterval = (): boolean => true;

/**
 * Deliberately not rotated.
 *
 * Rotating the month names was the obvious way to fit twelve of them on a phone, but the
 * axis reserves a fixed height and a label turned 45° needs more vertical room than that --
 * the chart keeps the tick and silently renders it with no text, so the months disappear
 * entirely. The scroller already gives every month about 80px of its own, which is more
 * than a short month name needs, so there is nothing left for rotation to solve.
 */
export const tickLabelStyleFor = (isNarrow: boolean): Record<string, unknown> => ({
    fontSize: isNarrow ? 10 : 11,
});

/** Taller than a plain bar chart needs, because the labels want air above the bars. */
export const chartHeightFor = (isNarrow: boolean): number => (isNarrow ? 300 : 360);

export const labelFontSizeFor = (isNarrow: boolean): number => (isNarrow ? 9 : 11);

/**
 * Grouped bars, not stacked — omitting `stack` is what does that. The gap ratios live on
 * the band axis rather than on the chart, and these are tighter than the defaults because
 * twelve groups have to share the width.
 */
export const CATEGORY_GAP_RATIO = 0.3;
export const BAR_GAP_RATIO = 0.05;

export const BAR_BORDER_RADIUS = 2;
